import { useEffect, useState, useCallback, PropsWithChildren } from 'react';
import { VictoryChart, VictoryContainer } from 'victory';

type Dimensions = {
  width: number;
  height: number;
};

export const useContainerDimensions = (): [(node: HTMLElement | null) => void, Dimensions] => {
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [node, setNode] = useState<HTMLElement | null>(null);

  const getDimensions = useCallback(() => {
    if (node) {
      return {
        width: node.offsetWidth,
        height: node.offsetHeight,
      };
    }
    return { width: 0, height: 0 };
  }, [node]);

  useEffect(() => {
    const handleResize = () => setDimensions(getDimensions());
    if (node) setDimensions(getDimensions());
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [getDimensions, node]);

  return [setNode, dimensions];
};

export default function ResponsiveVictoryChart({
  minWidth = 800,
  ...props
}: {
  minWidth?: number;
} & PropsWithChildren) {
  const [setNode, dimensions] = useContainerDimensions();

  return (
    <div style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
      <VictoryChart
        containerComponent={
          <VictoryContainer
            containerRef={setNode}
            style={{
              width: '100%',
              minWidth: minWidth + 'px',
              height: '300px',
              userSelect: 'none',
              pointerEvents: 'none',
              touchAction: 'auto',
              position: 'relative',
            }}
          />
        }
        width={dimensions.width}
        height={dimensions.height}
        {...props}
      />
    </div>
  );
}
